import { useState } from 'react'
import { createFileRoute, useNavigate, Link } from '@tanstack/react-router'
import { AppLayout } from '@/components/AppLayout'
import { TopBarBackIconButton } from '@/components/topbar'
import { DesktopPage } from '@/components/desktop/DesktopPage'
import { DesktopPageHeader } from '@/components/desktop/DesktopPageHeader'
import { useIsDesktop } from '@/components/ui/use-mobile'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { EmptyState } from '@/components/EmptyState'
import { ChooseChecklistTemplateDrawer } from '@/components/ChooseChecklistTemplateDrawer'
import { SearchInput } from '@/components/ui/search-input'
import { SectionTitle } from '@/components/ui/section-title'
import { FileCheck, Plus } from '@/components/icons'
import { useChecklistTemplates } from '@/lib/hooks'

export const Route = createFileRoute('/_auth/operations/checklisttemplates/')({
  component: ChecklistTemplatesRoute,
})

function ChecklistTemplatesRoute() {
  const navigate = useNavigate()
  const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState(false)
  const [search, setSearch] = useState('')

  const { data: templates, isLoading } = useChecklistTemplates()

  const query = search.trim().toLowerCase()
  const filtered = query
    ? (templates ?? []).filter((t) => t.name.toLowerCase().includes(query))
    : templates ?? []

  const isDesktop = useIsDesktop()

  const body = (
    <>
      <p className="pt-2 text-sm text-content-primary-bold">
        Create checklists for reports, flights, or any task.
      </p>

      <div className="sticky top-0 z-10 -mx-4 bg-surface-brand-L0 px-4 py-2">
        <SearchInput
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onClear={() => setSearch('')}
          placeholder="Search your checklists"
        />
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i} className="flex flex-col gap-3 px-4 py-4">
              <div className="flex flex-col gap-1">
                <Skeleton className="h-5 w-40" />
                <Skeleton className="h-3 w-28" />
              </div>
              <hr className="border-border-L1" />
              <div className="flex items-center justify-end gap-3">
                <Skeleton className="h-8 w-20 rounded-md" />
                <Skeleton className="h-8 w-16 rounded-md" />
              </div>
            </Card>
          ))}
        </div>
      ) : !templates?.length ? (
        <EmptyState
          icon={FileCheck}
          title="No checklists yet"
          description='Tap "Add Checklist" to start.'
          addButtonText="Add Checklist"
          onAdd={() => setIsCreateDrawerOpen(true)}
        />
      ) : (
        <div className="flex flex-col gap-3">
          <SectionTitle label="Your Checklists" count={filtered.length} className="pb-0" />

          {filtered.map((template) => (
            <Card key={template.id} className="flex flex-col gap-3 px-4 py-4">
              <div className="flex flex-col gap-1">
                <p className="text-base font-semibold tracking-[-0.5px]">{template.name}</p>
                <p className="text-xs text-content-primary-subtle">
                  {template.items.length} field{template.items.length !== 1 ? 's' : ''} ·{' '}
                  {template._count.tasks === 0
                    ? 'Unused'
                    : `Used ${template._count.tasks} time${template._count.tasks !== 1 ? 's' : ''}`}
                </p>
              </div>

              <hr className="border-border-L1" />

              <div className="flex items-center justify-end gap-3">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() =>
                    navigate({
                      to: '/operations/checklisttemplates/preview/$checklistId',
                      params: { checklistId: template.id },
                    })
                  }
                >
                  Preview
                </Button>
                <Button
                  size="sm"
                  variant="primary"
                  onClick={() =>
                    navigate({
                      to: '/operations/checklisttemplates/edit/$checklistId',
                      params: { checklistId: template.id },
                    })
                  }
                >
                  Edit
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <ChooseChecklistTemplateDrawer
        isOpen={isCreateDrawerOpen}
        onClose={() => setIsCreateDrawerOpen(false)}
      />
    </>
  )

  const breadcrumb = (
    <span>
      <Link to="/tasks" className="hover:underline">
        Checklists
      </Link>
      <span className="text-content-primary-subtle"> / Checklist Builder</span>
    </span>
  )

  const addAction = (
    <Button
      roundness="round"
      variant="brand"
      size="sm"
      className="gap-2"
      onClick={() => setIsCreateDrawerOpen(true)}
    >
      Add Checklist
      <Plus className="size-4 text-content-primary-white" />
    </Button>
  )

  if (isDesktop) {
    return (
      <DesktopPage>
        <DesktopPageHeader
          title="Checklist Builder"
          breadcrumb={breadcrumb}
          action={addAction}
        />
        {body}
      </DesktopPage>
    )
  }

  return (
    <AppLayout
      topBar={
        <TopBarBackIconButton
          title="Checklist Builder"
          icon={
            <Button roundness="round" variant="brand" size="sm" className="size-8 p-0">
              <Plus className="size-4 text-content-primary-white" />
            </Button>
          }
          onIconClick={() => setIsCreateDrawerOpen(true)}
        />
      }
    >
      {body}
    </AppLayout>
  )
}
